/**
 * Carrier MC verification tracking (Settings → General → Verify MC).
 *
 * Answers three questions in Amplitude (Squadron Squad):
 *   - which path did the carrier take?      → `method` (email / documents / migration) + `step`
 *   - how did the attempt end?              → mc_verification_succeeded vs mc_verification_failed
 *   - why did it fail?                      → `failure_reason`, a bounded bucket (never the raw server text)
 *
 * Cardinality discipline (mirrors tmsActions.ts): only enums, booleans and small counts.
 * No emails, MC/DOT numbers, file names, reviewer notes or raw error strings.
 * company_id / mc_number / project_name are stamped by the unified analytics context.
 */

import { CarrierVerificationErrorCode, McVerificationStatus } from '@numeo/types-core'
import { AnalyticsEvent, EventCategory } from './events'
import { PropertyValue } from './types'
import { trackUnifiedEvent } from './unified'

/**
 * Which verification path was used — mirrors McVerificationMethod, plus `migration` for the
 * multi-MC gate that must clear before verifying. `manual` is an internally granted badge.
 */
export type McVerificationMethodTag = 'email' | 'documents' | 'phone' | 'manual' | 'migration'

/** The concrete action inside the flow. `review` is the internal reviewer's decision, observed on refresh. */
export type McVerificationStepTag = 'send_code' | 'verify_code' | 'upload_documents' | 'migrate' | 'review'

/** Bounded failure buckets — derived from the server message, which itself never reaches Amplitude. */
export type McVerificationFailureReason =
    | 'rate_limited'
    | 'expired_code'
    | 'code_not_requested'
    | 'invalid_code'
    | 'no_fmcsa_record'
    | 'carrier_inactive'
    | 'email_not_verified'
    | 'mc_missing'
    | 'no_fmcsa_email'
    | 'unsupported_document'
    | 'documents_missing'
    | 'document_already_approved'
    | 'factoring_locked'
    | 'delivery_unavailable'
    | 'stale_state'
    | 'not_owner'
    | 'documents_rejected'
    | 'fmcsa_unavailable'
    | 'send_failed'
    | 'request_failed'
    | 'unknown'

/** Extra bounded dimensions a call site may attach (is_resend, document_count, uses_factoring, …). */
type Detail = Record<string, PropertyValue>

interface BaseParams {
    method: McVerificationMethodTag
    step: McVerificationStepTag
    detail?: Detail
}

// trackUnifiedEvent already guards the GA and Amplitude calls internally, so this catch is defence in
// depth rather than a live path — kept for parity with trackTmsAction, which wraps it the same way.
const emit = (action: AnalyticsEvent, props: Record<string, PropertyValue>): void => {
    try {
        trackUnifiedEvent({ category: EventCategory.FEATURE_USE, action, ...props })
    } catch {
        /* analytics must never break the page */
    }
}

/** The server's machine-readable code, read before any message text (codes the web flow never sends are absent). */
const FAILURE_REASON_BY_CODE: Partial<Record<CarrierVerificationErrorCode, McVerificationFailureReason>> = {
    [CarrierVerificationErrorCode.INVALID_NUMBER]: 'mc_missing',
    [CarrierVerificationErrorCode.RATE_LIMITED]: 'rate_limited',
    [CarrierVerificationErrorCode.TOO_MANY_TRIES]: 'rate_limited',
    [CarrierVerificationErrorCode.CODE_EXPIRED]: 'expired_code',
    [CarrierVerificationErrorCode.NO_CODE]: 'code_not_requested',
    [CarrierVerificationErrorCode.CODE_INCORRECT]: 'invalid_code',
    // The dashboards have always counted a malformed code as invalid_code (the '6-digit' text match); keep it there.
    [CarrierVerificationErrorCode.CODE_MALFORMED]: 'invalid_code',
    [CarrierVerificationErrorCode.CARRIER_NOT_FOUND]: 'no_fmcsa_record',
    [CarrierVerificationErrorCode.CARRIER_RECORD_UNCONFIRMED]: 'fmcsa_unavailable',
    [CarrierVerificationErrorCode.NO_CONTACT]: 'no_fmcsa_email',
    [CarrierVerificationErrorCode.AUTHORITY_INACTIVE]: 'carrier_inactive',
    [CarrierVerificationErrorCode.OUT_OF_SERVICE]: 'carrier_inactive',
    [CarrierVerificationErrorCode.LOOKUP_UNAVAILABLE]: 'fmcsa_unavailable',
    [CarrierVerificationErrorCode.SEND_FAILED]: 'send_failed',
}

/**
 * Buckets a caught error into a fixed reason: by the server's `errorCode` when it sent one, else by its message.
 * Matching order matters for the message: the rate-limit and expiry messages also contain words used by the
 * generic invalid-code branch.
 *
 * The message fallback matches copy owned by main-service (identity/organizations/mc-verification), so a reword
 * there degrades it to `unknown`; failures that carry an `errorCode` no longer depend on it.
 *
 * Same shape as `classifyVerificationError` in ./onboarding.ts (signup OTP) — different domain
 * and bucket set on purpose; if one gains a bucket, check whether the other needs it too.
 */
export const classifyMcVerificationFailure = (error: unknown): McVerificationFailureReason => {
    const errorCode = (error as { errorCode?: CarrierVerificationErrorCode } | undefined)?.errorCode
    const byCode = errorCode ? FAILURE_REASON_BY_CODE[errorCode] : undefined
    if (byCode) return byCode
    const message = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase()
    if (!message) return 'unknown'
    if (message.includes('too many') || message.includes('rate limit') || message.includes('429')) return 'rate_limited'
    if (message.includes('expired')) return 'expired_code'
    if (message.includes('no verification code found')) return 'code_not_requested'
    if (message.includes('invalid verification code') || message.includes('6-digit')) return 'invalid_code'
    if (message.includes('no fmcsa record')) return 'no_fmcsa_record'
    if (message.includes('no email on file')) return 'no_fmcsa_email'
    if (message.includes('inactive') || message.includes('out of service')) return 'carrier_inactive'
    if (message.includes('verify your fmcsa email first')) return 'email_not_verified'
    if (message.includes('mc or dot number')) return 'mc_missing'
    if (message.includes('unsupported document') || message.includes('unsupported file') || message.includes('10mb')) return 'unsupported_document'
    // Reachable from the factoring toggle in this same modal: the NOA requirement is locked
    // once documents are submitted. Checked before 'already approved' — different failure.
    if (message.includes('already marked as using factoring')) return 'factoring_locked'
    if (message.includes('already approved')) return 'document_already_approved'
    if (message.includes('at least one document is required') || message.includes('no pending document verification')) return 'documents_missing'
    if (message.includes('account email is unavailable')) return 'delivery_unavailable'
    if (message.includes('changed since you opened') || message.includes('refresh and try again')) return 'stale_state'
    if (message.includes('only the account owner')) return 'not_owner'
    // The API layer replaces transport/5xx failures with its own "Failed to …" fallback, so there is
    // no server text left to bucket — keep those separate from genuinely unclassified messages.
    if (message.startsWith('failed to')) return 'request_failed'
    return 'unknown'
}

/** Fires when the verify (or migrate-first) modal opens — the entry point of the funnel. */
export const trackMcVerificationFlowOpened = (props: {
    gate: 'verify' | 'migrate'
    entryStep: 'method' | 'email-code' | 'documents'
    fromStatus: McVerificationStatus
    hasMc: boolean
    emailVerified: boolean
    migrationRequired: boolean
    splitEligible: boolean
}): void =>
    emit(AnalyticsEvent.MC_VERIFICATION_FLOW_OPENED, {
        gate: props.gate,
        entry_step: props.entryStep,
        from_status: props.fromStatus,
        has_mc: props.hasMc,
        email_verified: props.emailVerified,
        migration_required: props.migrationRequired,
        split_eligible: props.splitEligible,
    })

/** Fires the moment a request goes out, so succeeded/failed always have a denominator. */
export const trackMcVerificationAttempted = ({ method, step, detail }: BaseParams): void =>
    emit(AnalyticsEvent.MC_VERIFICATION_ATTEMPTED, { method, step, ...detail })

/** `resultStatus` is omitted for steps that don't move the verification status (e.g. sending a code). */
export const trackMcVerificationSucceeded = ({ method, step, resultStatus, detail }: BaseParams & { resultStatus?: McVerificationStatus }): void =>
    emit(AnalyticsEvent.MC_VERIFICATION_SUCCEEDED, {
        method,
        step,
        outcome: 'success',
        ...(resultStatus ? { result_status: resultStatus } : {}),
        ...detail,
    })

export const trackMcVerificationFailed = ({
    method,
    step,
    error,
    reason,
    detail,
}: BaseParams & { error?: unknown; reason?: McVerificationFailureReason }): void =>
    emit(AnalyticsEvent.MC_VERIFICATION_FAILED, {
        method,
        step,
        outcome: 'failure',
        failure_reason: reason ?? classifyMcVerificationFailure(error),
        ...detail,
    })

// Terminal outcomes (VERIFIED / REJECTED) are decided server-side by a reviewer and only observed when
// /status is polled — which happens on every mount and after every action. These guards keep one event
// per real decision. In-memory first (covers a single session even with storage blocked), localStorage
// second (survives reloads). The marker is part of the key so a re-review after a re-upload fires again.
//
// Keys are written once per decision per workspace and never pruned: intentional. The set is bounded by
// (workspaces the user belongs to) × (decisions on each), i.e. single digits for a normal account, and
// each entry is well under 100 bytes. There is nothing to expire them against — a swept key would just
// let an already-reported decision be reported again.
const firedOutcomes = new Set<string>()

export const claimMcVerificationOutcome = (workspaceId: string, status: McVerificationStatus, marker: string): boolean => {
    const key = `analytics_mc_verification_outcome:${workspaceId}:${status}:${marker}`
    if (firedOutcomes.has(key)) return false
    firedOutcomes.add(key)
    try {
        if (localStorage.getItem(key) === '1') return false
        localStorage.setItem(key, '1')
    } catch {
        /* storage unavailable — the in-memory guard still covers this session */
    }
    return true
}
